import React, { SyntheticEvent, useState } from "react";
import {
    Grid,
    TextField,
    MenuItem,
    Button,
    Card,
    CardContent,
    makeStyles,
    createStyles,
    Theme,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
} from "@material-ui/core";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import ProgressSaveButton from "../../../../components/_Layout/Buttons/ProgressSaveButton";
import CustomCardTitle from "../../../../interfaces/common/card/CustomCardTitle";
import { ThemeTextField } from "../../../../components/_Layout/Inputs/ThemeTextField";
import { FieldError, FormContext, useForm } from "react-hook-form";
import { Autocomplete } from "@material-ui/lab";
import * as PaymentPlanApi from '../../../../api/student/financials/paymentPlan/paymentPlanApi';
import { IStudentPaymentPlan, IStudentPaymentPlanSchedule } from "../../../../interfaces/student/financials/IStudentPaymentPlan";
import { SnackBarStateProps } from "../../profile/studentInfo/StudentInfo";
import CustomSnackbar from "../../../../components/notifications/CustomSnackbar";
import { CustomSnackBarProps } from "../../../../interfaces/common/CustomSnackBarProps";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { EmptyGuid } from "../../../../utils/constants";
import autoTable from "jspdf-autotable";

const CreatePaymentPlan = ({
    onClose,
    selectedEnrollment,
    paymentPlan
}: {
    onClose: (newPlanId?: string) => void;
    selectedEnrollment: any;
    paymentPlan?: IStudentPaymentPlan | null;
}) => {
    const data = useForm<any>({ mode: "onBlur" });

    const [loaderState, setLoaderState] = React.useState<boolean>(false);
    const [paymentRows, setPaymentRows] = useState<any[]>([]);
    const [showStudentPlan, setShowStudentPlan] = useState(false);

    type SnackBarStateProps = {
        showSnackBar: boolean;
        messageInfo: string | undefined;
        variant: "success" | "error" | "warning" | "info";
    };

    const [snackBarProps, setSnackBarProps] = React.useState<SnackBarStateProps>({
        showSnackBar: false,
        messageInfo: undefined,
        variant: "info",
    });

    const [model, setModel] = React.useState<IStudentPaymentPlan>({
        paymentPlanId: EmptyGuid,
        studentEnrollmentId: selectedEnrollment?.stuEnrollId || "",
        startDate: null,
        endDate: null,
        academicYearId: null,
        totalAmountDue: 0,
        description: "",
        disbursements: null,
        awardId: null,
        paymentInterval: "Monthly",
        installmentMethod: "Number of Payments",
        numberOfPayment: 0,
        paymentAmount: 0,
        planSchedule: [],
    });

    React.useEffect(() => {
        if (paymentPlan) {
            setModel({
                ...paymentPlan,
                startDate: paymentPlan.startDate ? new Date(paymentPlan.startDate) : null,
                endDate: paymentPlan.endDate ? new Date(paymentPlan.endDate) : null,
            });
        }
    }, [paymentPlan]);

    const handleChange = (field: string, value: any) => {
        setModel((prev) => ({ ...prev, [field]: value }));
    };

    const calculatePayments = () => {
        const startValue = model.startDate;
        if (!startValue) {
            setPaymentRows([]);
            setShowStudentPlan(false);
            return;
        }

        const start = new Date(startValue);
        if (isNaN(start.getTime())) {
            setPaymentRows([]);
            setShowStudentPlan(false);
            return;
        }

        const total = Number(model.totalAmountDue);
        const input = Number(model.paymentAmount);

        if (total <= 0) {
            setPaymentRows([]);
            setShowStudentPlan(false);
            return;
        }

        if (input <= 0 || !model.installmentMethod) {
            alert(
                model.installmentMethod === "Number of Payments"
                    ? "Please enter the Number of Payments."
                    : "Please enter the payment amount."
            );
            setPaymentRows([]);
            setShowStudentPlan(false);
            return;
        }

        let count: number, size: number;
        if (model.installmentMethod === "Number of Payments") {
            count = input;
            size = total / count;
        } else {
            size = input;
            count = Math.ceil(total / size);
        }

        if (count <= 0 || size <= 0) {
            setPaymentRows([]);
            setShowStudentPlan(false);
            return;
        }

        const addInterval = (d: Date) => {
            const nxt = new Date(d);
            if (model.paymentInterval === "Weekly") nxt.setDate(nxt.getDate() + 7);
            else if (model.paymentInterval === "Bi-Monthly") nxt.setDate(nxt.getDate() + 15);
            else nxt.setMonth(nxt.getMonth() + 1);
            return nxt;
        };

        const rows: any[] = [];
        const planSchedule: IStudentPaymentPlanSchedule[] = [];
        let balance = total;
        let cursor = start;

        for (let i = 0; i < count; i++) {
            let paid;
            if (i === count - 1) {
                paid = balance;
            } else {
                paid = Math.min(size, balance);
            }
            paid = Math.round(paid * 100) / 100;
            balance = Math.round((balance - paid) * 100) / 100;

            rows.push({
                scheduledDate: cursor.toLocaleDateString("en-US").replace(/^0+/, '').replace(/\/0+/g, '/'),
                reference: "",
                projectedAmount: paid.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD"
                }),
                balance: balance > 0
                    ? balance.toLocaleString("en-US", { style: "currency", currency: "USD" })
                    : "$0.00"
            });

            planSchedule.push({
                scheduleId: EmptyGuid,
                paymentPlanId: model.paymentPlanId,
                expectedDate: new Date(cursor),
                amount: paid,
                balance: balance > 0 ? balance : 0,
                receivedAmount: 0,
                reference: "",
                modifiedUser: "",
                modifiedDate: new Date().toISOString(),
                transactionId: null
            });

            model.numberOfPayment = count;
            model.planSchedule = planSchedule;

            cursor = addInterval(cursor);
        }

        setPaymentRows(rows);
        setShowStudentPlan(true);
    };

    const handleExportPDF = () => {
        const doc = new jsPDF({ unit: "pt", format: "letter" });
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 40;
        const y0 = 60;
        const lineHeight = 16;

        doc.setFont("Times", "Bold").setFontSize(16);
        doc.text("Student Payment Plan Report", pageWidth / 2, 40, {
            align: "center",
        });

        doc.setFont("Times", "Normal").setFontSize(11);
        const leftLabelX = margin;
        const leftValueX = margin + 120;
        const rightLabelX = pageWidth - margin - 200;
        const rightValueX = pageWidth - margin;

        doc.text("Student Name:", leftLabelX, y0);
        doc.text(selectedEnrollment?.studentName || "", leftValueX, y0);
        doc.text("Student ID:", leftLabelX, y0 + lineHeight);
        doc.text(selectedEnrollment?.studentId || "", leftValueX, y0 + lineHeight);
        doc.text("Start Date:", leftLabelX, y0 + lineHeight * 2);
        doc.text(
            model.startDate
                ? new Date(model.startDate).toLocaleDateString("en-US")
                : "",
            leftValueX,
            y0 + lineHeight * 2
        );

        // Right column (right-aligned values)
        doc.text("Payment Plan Description:", rightLabelX, y0);
        doc.text(model.description || "", rightValueX, y0, { align: "right" });
        doc.text("Total Amount Due:", rightLabelX, y0 + lineHeight);
        doc.text(
            `$${model.totalAmountDue?.toLocaleString() || "0.00"}`,
            rightValueX,
            y0 + lineHeight,
            { align: "right" }
        );
        doc.text("Number of Payment:", rightLabelX, y0 + lineHeight * 2);
        doc.text(
            `${model.numberOfPayment || 0}`,
            rightValueX,
            y0 + lineHeight * 2,
            { align: "right" }
        );
        doc.text("Payment Plan Start:", rightLabelX, y0 + lineHeight * 3);
        doc.text(
            model.startDate
                ? new Date(model.startDate).toLocaleDateString("en-US")
                : "",
            rightValueX,
            y0 + lineHeight * 3,
            { align: "right" }
        );
        doc.text("Payment Plan Balance:", rightLabelX, y0 + lineHeight * 4);
        doc.text(
            `$${model.planSchedule?.[
                model.planSchedule.length - 1
            ]?.balance?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }) || "0.00"}`,
            rightValueX,
            y0 + lineHeight * 4,
            { align: "right" }
        );

        const hrY = y0 + lineHeight * 5 + 4;
        doc.setLineWidth(0.5).line(margin, hrY, pageWidth - margin, hrY);

        const tableWidth = pageWidth - margin * 2;
        autoTable(doc, {
            startY: hrY + 10,
            head: [["Scheduled Date", "Reference", "Projected Amount"]],
            body: paymentRows.map((r) => [
                r.scheduledDate
                    ? new Date(r.scheduledDate).toLocaleDateString("en-US")
                    : "",
                r.reference || "",
                r.projectedAmount || "",
            ]),
            theme: "grid",
            margin: { left: margin, right: margin },
            tableWidth,
            headStyles: {
                font: "Times",
                fontStyle: "bold",
                fontSize: 11,
                textColor: [0, 0, 0],
                fillColor: [255, 255, 255],
                lineWidth: 0.1,
            },
            bodyStyles: { font: "Times", fontSize: 10 },
            columnStyles: {
                0: { cellWidth: tableWidth * 0.3 },
                1: { cellWidth: tableWidth * 0.4, halign: "center" },
                2: { cellWidth: tableWidth * 0.3, halign: "right" },
            },
        });

        // Signature line
        const finalY = (doc as any).lastAutoTable.finalY + 40;
        doc.setLineWidth(0.5).line(200, finalY, pageWidth - 200, finalY);
        doc.setFontSize(11).text(
            "Student Account Officer Signature",
            pageWidth / 2,
            finalY + 15,
            { align: "center" }
        );

        doc.save("PaymentPlanPreview.pdf");
    };



    const amount = model.paymentAmount ?? 0;

    const canSave =
        model.description.trim().length > 0 &&
        model.totalAmountDue > 0 &&
        amount > 0

    const handleSave = () => {
        if (model) {
            setLoaderState(true);
            PaymentPlanApi.AddPaymentPlan(model).then(
                (response: any) => {
                    setLoaderState(false);
                    if (response.resultStatus === 0) {
                        setSnackBarProps((props: any) => {
                            return {
                                variant: "success",
                                showSnackBar: true,
                                messageInfo: "Payment Plan info saved successfully.",
                            };
                        });
                        setTimeout(() => {
                            onClose();
                        }, 2000);

                    } else {
                        setSnackBarProps((props: any) => {
                            return {
                                variant: "error",
                                showSnackBar: true,
                                messageInfo: "Payment Plan info not saved",
                            };
                        });
                    }
                },
                (exception: any) => {
                    setLoaderState(false);
                    setSnackBarProps((props: any) => {
                        return {
                            variant: "error",
                            showSnackBar: true,
                            messageInfo: exception.message || "An error occurred while updating the payment plan.",
                        };
                    });
                }
            );
        }
    };

    return (
        <>
            <FormContext {...data}>
                <form>
                    <Card>
                        <CustomCardTitle title="Payment Plan" />
                        <CardContent>

                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <ThemeTextField
                                        label="Student Enrollment"
                                        value={selectedEnrollment.programVersionDescription}
                                        onChange={(e) => handleChange("studentEnrollmentId", e.target.value)}
                                        fullWidth
                                        required
                                        disabled={true}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <ThemeTextField
                                        id="description"
                                        name="description"
                                        label="Payment Plan Description"
                                        value={model.description}
                                        onChange={(e) => handleChange("description", e.target.value)}
                                        fullWidth
                                        required
                                        inputRef={data.register({
                                            required: true
                                        })}
                                        helperText={
                                            data.errors.description ? 'Description is required' : undefined
                                        }
                                        inputProps={{ maxLength: 50 }}
                                        error={!!data.errors.description}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                        <KeyboardDatePicker
                                            disableToolbar
                                            variant="inline"
                                            format="MM/dd/yyyy"
                                            id="startDate"
                                            name="startDate"
                                            label="Payment Plan Start Date"
                                            value={model.startDate ? model.startDate : null}
                                            onChange={(e: any, value: any) =>
                                                handleChange('startDate', value ? value : undefined)
                                            }
                                            KeyboardButtonProps={{
                                                'aria-label': 'start date',
                                            }}

                                            inputRef={data.register({ required: true })}
                                            helperText={
                                                data.errors["startDate"]
                                                    ? (data.errors["startDate"] as FieldError).message
                                                    : undefined
                                            }
                                            error={!!data.errors["startDate"]}
                                            autoOk={true}
                                            fullWidth
                                            required={true}
                                        />
                                    </MuiPickersUtilsProvider>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <ThemeTextField
                                        id="totalAmountDue"
                                        key="totalAmountDue"
                                        name="totalAmountDue"
                                        label="Total Amount Due*"
                                        error={!!data.errors.totalAmountDue}
                                        inputRef={data.register({ required: true })}
                                        helperText={
                                            data.errors.totalAmountDue ? 'Total amount is required' : undefined
                                        }
                                        value={model.totalAmountDue ?? 0.00}
                                        onChange={(e: any) => {
                                            handleChange('totalAmountDue', e.target.value);
                                        }}
                                        fullWidth
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        id="paymentInterval"
                                        autoComplete
                                        includeInputInList
                                        options={[
                                            { id: "Monthly", label: "Monthly" },
                                            { id: "Bi-Monthly", label: "Bi-Monthly" },
                                            { id: "Weekly", label: "Weekly" },
                                        ]}
                                        value={
                                            model.paymentInterval
                                                ? { id: model.paymentInterval, label: model.paymentInterval }
                                                : { id: "Monthly", label: "Monthly" }
                                        }
                                        onChange={(e: any, v: any) => {
                                            handleChange("paymentInterval", v ? v.id : "");
                                        }}
                                        getOptionLabel={(option) => option.label || ""}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Payment Interval*"
                                                name="paymentInterval"
                                                inputRef={data.register({ required: true })}
                                                fullWidth
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>

                        </CardContent>

                        <CustomCardTitle title="Plan Detail" />
                        <CardContent>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        id="installmentMethod"
                                        autoComplete
                                        includeInputInList
                                        options={[
                                            { id: "Number of Payments", label: "Number of Payments" },
                                            { id: "Set Payment Amount", label: "Set Payment Amount" },
                                        ]}
                                        value={
                                            model.installmentMethod
                                                ? { id: model.installmentMethod, label: model.installmentMethod }
                                                : { id: "Number of Payments", label: "Number of Payments" }
                                        }
                                        onChange={(e: any, v: any) => {
                                            handleChange("installmentMethod", v ? v.id : "");
                                        }}
                                        getOptionLabel={(option) => option.label || ""}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Installment Method*"
                                                name="installmentMethod"
                                                inputRef={data.register({ required: true })}
                                                fullWidth
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        label={model.installmentMethod == "Set Payment Amount" ? "Payment Amount" : "Number of Payments"}
                                        value={model.paymentAmount}
                                        onChange={(e: any) => {
                                            const value = e.target.value === "" ? 0 : Number(e.target.value);
                                            handleChange('paymentAmount', value);
                                        }}
                                        fullWidth
                                        required
                                    />
                                </Grid>

                                <Grid item xs={12} sm={3} style={{ display: "flex", alignItems: "center" }}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        size="small"
                                        style={{ minWidth: 150, height: 36 }}
                                        onClick={calculatePayments}
                                    >
                                        Calculate Payments
                                    </Button>
                                </Grid>

                                <Grid item xs={12}>
                                    <Grid container spacing={2} justifyContent="flex-start">
                                        <Grid item>
                                            <ProgressSaveButton
                                                text="Save"
                                                onClick={handleSave}
                                                loading={false}
                                                type="button"
                                                disabled={loaderState || !canSave}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <Button variant="contained" onClick={() => onClose()}>
                                                Cancel
                                            </Button>
                                        </Grid>

                                    </Grid>
                                </Grid>
                            </Grid>
                        </CardContent>

                        {showStudentPlan && (
                            <>
                                <CustomCardTitle title="Student Payment Plan" />

                                <Grid container alignItems="center" style={{ marginBottom: 8 }}>
                                    <Grid item>
                                        <Button
                                            variant="contained"
                                            color="default"
                                            startIcon={<span className="fa fa-file-pdf-o" />}
                                            onClick={handleExportPDF}
                                        >
                                            EXPORT TO PDF
                                        </Button>
                                    </Grid>
                                </Grid>
                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Scheduled Date</TableCell>
                                                <TableCell>Reference</TableCell>
                                                <TableCell>Projected Amount</TableCell>
                                                <TableCell>Balance</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paymentRows.map((row, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>{row.scheduledDate}</TableCell>
                                                    <TableCell>{row.reference}</TableCell>
                                                    <TableCell>{row.projectedAmount}</TableCell>
                                                    <TableCell>{row.balance}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}
                    </Card>
                </form>
            </FormContext>
            <CustomSnackbar
                variant={snackBarProps.variant}
                message={snackBarProps.messageInfo}
                open={snackBarProps.showSnackBar}
                onClose={(event?: React.SyntheticEvent, reason?: string) => {
                    setSnackBarProps((props: any) => {
                        return { ...props, showSnackBar: false };
                    });
                }}
            ></CustomSnackbar>
        </>
    );
};

export default CreatePaymentPlan;
